import { Info } from '@phosphor-icons/react'
import React, { useEffect, useState } from 'react'
import SaveNotification from 'src/components/common/SaveNotification'
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { alertSeverites, apiURI } from 'src/lib/utils/constants'
import { successToast } from 'src/lib/utils/toasters'
import { getRequest, patchRequest } from 'src/lib/core/api-client'
import { errorToast } from 'src/lib/utils/toasters'
import { setDashboardConfig } from 'src/lib/core/api-methods'
import { useLocale } from 'src/lib/hooks/useLocal'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'

export const AdditionalScript = () => {
  const { t } = useLocale()
  const { integrations, setIntegrations } = useIntegrations()
  const { sendActivityLog } = useActivityLogs()

  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)
  const [additionalScripts, setAdditionalScripts] = useState<string>(integrations.additionalScripts.script)

  useEffect(() => {
    setSettingsUpdated(
      Boolean(JSON.stringify(integrations.additionalScripts.script) !== JSON.stringify(additionalScripts))
    )
  }, [integrations.additionalScripts.script, additionalScripts])

  useEffect(() => {
    const fetchAdditionalScripts = async () => {
      const response: any = await getRequest('/dashboard/thank-you-page/config', apiURI.CHECKOUT_NODE)
      if (response?.error) {
        errorToast(response?.error ?? 'Error fetching Additional script config')
      } else {
        setAdditionalScripts(response?.data?.script)
        setIntegrations({ ...integrations, additionalScripts: { script: response?.data?.script } })
      }
    }
    fetchAdditionalScripts()
  }, [])

  const saveAdditionalScripts = async () => {
    try {
      const response: any = await setDashboardConfig('thank-you-page', {
        script: additionalScripts
      })
      if (response?.error) {
        errorToast(response?.error ?? 'Error saving Additional script config')
      } else {
        setIntegrations({ ...integrations, additionalScripts: { script: additionalScripts } })
        sendActivityLog({
          entityName: EntityName.SETTINGS,
          actionName: 'Updated Additional script',
          metadata: {
            script: additionalScripts
          }
        })
        successToast(t('thank_you_screen.success_toast'))
      }
    } catch (error: any) {
      errorToast(error.message ?? 'Error saving Additional script config')
    }
  }

  const handleDiscardSaving = () => {
    setAdditionalScripts(integrations.additionalScripts.script)
  }

  return (
    <>
      <div className='flex flex-col gap-4'>
        <p className='text-sm text-coal-light'>{t('thank_you_screen.manage_scripts')}</p>
        <div className='p-6 bg-white rounded-lg flex flex-col gap-4'>
          <div className='flex items-center gap-2 p-3 bg-yellow-faintest border border-yellow-faint rounded-lg'>
            <Info size={16} className='text-yellow-dark flex-shrink-0' />
            <p className='text-sm text-yellow-dark'>{t('thank_you_screen.info_text')}</p>
          </div>
          <div>
            <div className='flex items-center gap-2 mb-2'>
              <label htmlFor='scripts' className='block text-sm font-medium text-[#6e6e6e]'>
                {t('thank_you_screen.additional_scripts')}
              </label>
            </div>
            <textarea
              id='scripts'
              className='w-full h-64 p-3 border border-gray-light rounded-xl text-sm text-coal-light font-medium resize-none transition-all duration-200 focus:outline-none focus:border-primary-dark focus:shadow-[0_0_0_2.5px_rgba(1,204,167,0.25)] hover:bg-gray-light focus:bg-white'
              placeholder={t('thank_you_screen.placeholder')}
              style={{ fontFamily: 'Inter, sans-serif' }}
              value={additionalScripts}
              onChange={e => setAdditionalScripts(e.target.value)}
            />
          </div>
        </div>
      </div>
      <SaveNotification
        isVisible={settingsUpdated}
        onSave={saveAdditionalScripts}
        onDiscard={handleDiscardSaving}
        className='w-full'
      />
    </>
  )
}
